// src/pages/DanceStyles.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase';

const EMPTY_FORM = {
  id: '',
  label: '',
  parentId: '',
  order: 0,
  active: true,
};

function slugify(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function DanceStyles() {
  const [styles, setStyles] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadStyles = async () => {
    setLoading(true);
    setError('');

    try {
      const snapshot = await getDocs(collection(db, 'danceStyles'));
      const rows = snapshot.docs
        .map((styleDoc) => ({ id: styleDoc.id, ...styleDoc.data() }))
        .sort((a, b) => {
          const parentCompare = String(a.parentId || '').localeCompare(String(b.parentId || ''));
          if (parentCompare !== 0) return parentCompare;

          const orderCompare = (Number(a.order) || 0) - (Number(b.order) || 0);
          if (orderCompare !== 0) return orderCompare;

          return String(a.label || a.id).localeCompare(String(b.label || b.id), 'fr');
        });

      setStyles(rows);
    } catch (err) {
      console.error(err);
      setError('Impossible de charger les styles de danse.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStyles();
  }, []);

  const parentStyles = useMemo(
    () => styles.filter((style) => !style.parentId && style.id !== editingId),
    [styles, editingId]
  );

  const groupedStyles = useMemo(() => {
    const parents = styles.filter((style) => !style.parentId);

    const groups = parents.map((parent) => ({
      parent,
      children: styles.filter((style) => style.parentId === parent.id),
    }));

    const orphanChildren = styles.filter(
      (style) => style.parentId && !styles.some((parent) => parent.id === style.parentId)
    );

    return { groups, orphanChildren };
  }, [styles]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId('');
  };

  const handleEdit = (style) => {
    setEditingId(style.id);
    setForm({
      id: style.id,
      label: style.label || '',
      parentId: style.parentId || '',
      order: Number(style.order) || 0,
      active: style.active !== false,
    });
    setError('');
    setSuccess('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    const label = form.label.trim();
    const requestedId = editingId || form.id.trim() || slugify(label);
    const finalId = slugify(requestedId);

    if (!label) {
      setError('Le nom du style est obligatoire.');
      return;
    }

    if (!finalId) {
      setError("L'identifiant du style n'est pas valide.");
      return;
    }

    if (form.parentId === finalId) {
      setError('Un style ne peut pas être sa propre catégorie parente.');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        label,
        parentId: form.parentId || null,
        order: Number(form.order) || 0,
        active: Boolean(form.active),
        updatedAt: new Date().toISOString(),
      };

      if (editingId) {
        await updateDoc(doc(db, 'danceStyles', editingId), payload);
        setSuccess('Style modifié avec succès.');
      } else {
        await setDoc(doc(db, 'danceStyles', finalId), {
          ...payload,
          createdAt: new Date().toISOString(),
        });
        setSuccess('Style ajouté avec succès.');
      }

      resetForm();
      await loadStyles();
    } catch (err) {
      console.error(err);
      setError(
        err?.code === 'permission-denied'
          ? "Permission refusée. Vérifie les règles Firestore pour la collection danceStyles."
          : `Erreur lors de l'enregistrement : ${err.message}`
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (style) => {
    setError('');
    setSuccess('');

    try {
      await updateDoc(doc(db, 'danceStyles', style.id), {
        active: style.active === false,
        updatedAt: new Date().toISOString(),
      });
      await loadStyles();
    } catch (err) {
      console.error(err);
      setError("Impossible de modifier le statut du style.");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    const hasChildren = styles.some((style) => style.parentId === deleteTarget.id);
    if (hasChildren) {
      setError(
        "Cette catégorie contient encore des sous-catégories. Supprime-les ou déplace-les avant de supprimer la catégorie."
      );
      setDeleteTarget(null);
      return;
    }

    try {
      await deleteDoc(doc(db, 'danceStyles', deleteTarget.id));
      setSuccess('Style supprimé.');
      setDeleteTarget(null);
      if (editingId === deleteTarget.id) resetForm();
      await loadStyles();
    } catch (err) {
      console.error(err);
      setError(`Impossible de supprimer le style : ${err.message}`);
    }
  };

  const StyleRow = ({ style, isChild = false }) => (
    <Box
      key={style.id}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 1fr) 130px 130px 180px' },
        gap: 1.5,
        alignItems: 'center',
        px: 2,
        py: 1.5,
        ml: isChild ? { xs: 2, md: 5 } : 0,
        borderTop: '1px solid #ececec',
        bgcolor: isChild ? '#fafafa' : '#fff',
      }}
    >
      <Box>
        <Typography sx={{ fontWeight: isChild ? 500 : 700 }}>
          {isChild ? '↳ ' : ''}{style.label || style.id}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ID : {style.id}
          {style.parentId ? ` · Parent : ${style.parentId}` : ' · Catégorie principale'}
        </Typography>
      </Box>

      <Typography variant="body2">Ordre : {Number(style.order) || 0}</Typography>

      <FormControlLabel
        sx={{ m: 0 }}
        control={
          <Switch
            checked={style.active !== false}
            onChange={() => toggleActive(style)}
            size="small"
          />
        }
        label={style.active !== false ? 'Actif' : 'Inactif'}
      />

      <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 1 }}>
        <IconButton aria-label={`Modifier ${style.label}`} onClick={() => handleEdit(style)}>
          <EditIcon />
        </IconButton>
        <IconButton
          color="error"
          aria-label={`Supprimer ${style.label}`}
          onClick={() => setDeleteTarget(style)}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Styles de danse
          </Typography>
          <Typography color="text.secondary">
            Ces styles sont utilisés par l’admin et par le planning du site.
          </Typography>
        </Box>

        <Button startIcon={<RefreshIcon />} onClick={loadStyles} disabled={loading}>
          Actualiser
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper component="form" onSubmit={handleSubmit} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
          {editingId ? 'Modifier le style' : 'Ajouter un style'}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.3fr 1fr 1fr 140px' },
            gap: 2,
          }}
        >
          <TextField
            label="Nom du style *"
            value={form.label}
            onChange={(event) => {
              const label = event.target.value;
              setForm((previous) => ({
                ...previous,
                label,
                id: editingId ? previous.id : slugify(label),
              }));
            }}
            required
          />

          <TextField
            label="Identifiant Firestore"
            value={form.id}
            onChange={(event) => setForm((previous) => ({ ...previous, id: event.target.value }))}
            disabled={Boolean(editingId)}
            helperText={editingId ? "L'identifiant ne peut pas être changé." : 'Ex. streetdance-hip-hop'}
          />

          <TextField
            select
            label="Catégorie parente"
            value={form.parentId}
            onChange={(event) => setForm((previous) => ({ ...previous, parentId: event.target.value }))}
          >
            <MenuItem value="">Aucune — catégorie principale</MenuItem>
            {parentStyles.map((style) => (
              <MenuItem key={style.id} value={style.id}>
                {style.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            type="number"
            label="Ordre"
            value={form.order}
            onChange={(event) => setForm((previous) => ({ ...previous, order: event.target.value }))}
            inputProps={{ min: 0 }}
          />
        </Box>

        <FormControlLabel
          sx={{ mt: 2 }}
          control={
            <Checkbox
              checked={form.active}
              onChange={(event) => setForm((previous) => ({ ...previous, active: event.target.checked }))}
            />
          }
          label="Style actif"
        />

        <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
          <Button
            type="submit"
            variant="contained"
            startIcon={<AddIcon />}
            disabled={saving}
          >
            {saving ? 'Enregistrement...' : editingId ? 'Enregistrer les modifications' : 'Ajouter'}
          </Button>

          {editingId && (
            <Button type="button" onClick={resetForm}>
              Annuler
            </Button>
          )}
        </Box>
      </Paper>

      <Paper sx={{ overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.5, bgcolor: '#f5f5f5' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Liste des styles ({styles.length})
          </Typography>
        </Box>

        {loading ? (
          <Typography sx={{ p: 3 }}>Chargement...</Typography>
        ) : styles.length === 0 ? (
          <Typography sx={{ p: 3 }}>Aucun style enregistré.</Typography>
        ) : (
          <>
            {groupedStyles.groups.map(({ parent, children }) => (
              <React.Fragment key={parent.id}>
                <StyleRow style={parent} />
                {children.map((child) => (
                  <StyleRow key={child.id} style={child} isChild />
                ))}
              </React.Fragment>
            ))}

            {groupedStyles.orphanChildren.length > 0 && (
              <>
                <Alert severity="warning" sx={{ m: 2 }}>
                  Certains styles référencent une catégorie parente inexistante.
                </Alert>
                {groupedStyles.orphanChildren.map((style) => (
                  <StyleRow key={style.id} style={style} isChild />
                ))}
              </>
            )}
          </>
        )}
      </Paper>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Supprimer ce style ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Le style « {deleteTarget?.label} » sera supprimé de Firestore. Les anciens cours
            qui contiennent encore son identifiant devront être corrigés manuellement.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Annuler</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DanceStyles;
